import { useCallback, useEffect, useRef, useState } from 'react'
import { STATUS_SESSION_CHANGED_EVENT } from '@/infra/sse/constants'
import type { SessionStatusChangedPayload } from '@/infra/sse/status/types'

export type TransitionType = 'resuming' | 'terminating'

export interface TransitioningSessionsState {
  readonly transitioningSessionIds: ReadonlyMap<string, TransitionType>
  readonly markTransitioning: (sessionId: string, type: TransitionType) => void
  readonly clearTransitioning: (sessionId: string) => void
  readonly isTransitioning: (sessionId: string) => boolean
  readonly getTransitionType: (sessionId: string) => TransitionType | null
}

export function useTransitioningSessionsState(): TransitioningSessionsState {
  const [transitioningSessionIds, setTransitioningSessionIds] = useState<
    ReadonlyMap<string, TransitionType>
  >(new Map())
  const transitioningIdsRef = useRef(transitioningSessionIds)
  transitioningIdsRef.current = transitioningSessionIds

  const clearTransitioning = useCallback((sessionId: string) => {
    setTransitioningSessionIds((prev) => {
      if (!prev.has(sessionId)) return prev
      const next = new Map(prev)
      next.delete(sessionId)
      return next
    })
  }, [])

  const markTransitioning = useCallback((sessionId: string, type: TransitionType) => {
    setTransitioningSessionIds((prev) => {
      if (prev.get(sessionId) === type) return prev
      return new Map(prev).set(sessionId, type)
    })
  }, [])

  const isTransitioning = useCallback(
    (sessionId: string) => transitioningSessionIds.has(sessionId),
    [transitioningSessionIds],
  )

  const getTransitionType = useCallback(
    (sessionId: string): TransitionType | null => transitioningSessionIds.get(sessionId) ?? null,
    [transitioningSessionIds],
  )

  useEffect(() => {
    const handleStatusChanged = (event: Event) => {
      const { detail } = event as CustomEvent<SessionStatusChangedPayload>
      if (transitioningIdsRef.current.has(detail.claudeSessionId)) {
        clearTransitioning(detail.claudeSessionId)
      }
    }

    globalThis.addEventListener(STATUS_SESSION_CHANGED_EVENT, handleStatusChanged)
    return () => {
      globalThis.removeEventListener(STATUS_SESSION_CHANGED_EVENT, handleStatusChanged)
    }
  }, [clearTransitioning])

  return {
    transitioningSessionIds,
    markTransitioning,
    clearTransitioning,
    isTransitioning,
    getTransitionType,
  }
}
